import {
  ButtonGroup,
  Card,
  closeCurrentDialog,
  CustomRequestOption,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  isFile,
  Upload,
  UploadState,
} from 'platform/components';
import {VStack} from 'platform/foundation';
import {match} from 'ts-pattern';
import {z} from 'zod';

import {useState} from 'react';

import {always} from 'ramda';

import {useUploadFileMutation} from '@dms/api/base';
import {
  DefinitionWithTemplatesResponseBody,
  TemplateResponseBody,
  usePatchTemplateV3Mutation,
} from '@dms/api/core';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {DOCX_MIME_TYPE, HTML_MIME_TYPE} from 'shared/web';

interface EditTemplateFormProps {
  authorizationProfileId: string | null;
  documentCode: string;
  documentType: DefinitionWithTemplatesResponseBody['type'];
  template: TemplateResponseBody;
}

type FormValues = {
  title: string;
  note?: string | null;
};

type File = {
  fileUri: string;
  fileName: string;
  fileId: string;
  isPrefilled: boolean;
};

export function EditTemplateForm(props: EditTemplateFormProps) {
  const [uploadFile, {isLoading}] = useUploadFileMutation();
  const [patchTemplate] = usePatchTemplateV3Mutation();

  const [file, setFile] = useState<File | null>(null);

  const handleSubmit: FormSubmitHandler<FormValues> = async (values) =>
    await patchTemplate({
      templateId: props.template.id,
      patchTemplateRequestBody: {
        title: values.title,
        note: values.note || null,
        fileId: file?.fileId ?? undefined,
      },
    })
      .unwrap()
      .then(() => {
        closeCurrentDialog();
      })
      .catch(handleApiError);

  const handleUploadFile = async ({file}: CustomRequestOption) => {
    if (!isFile(file)) {
      return;
    }

    await uploadFile({file})
      .unwrap()
      .then((data) => {
        setFile({
          fileName: data.file.name,
          fileUri: data.fileUri,
          fileId: data.fileId,
          isPrefilled: false,
        });
      })
      .catch(handleApiError);
  };

  return (
    <Form<FormValues>
      onSubmit={handleSubmit}
      defaultValues={{title: props.template.title, note: props.template.note ?? null}}
      experimentalZodSchema={schema}
    >
      {(control, formApi) => {
        const title = formApi.watch('title');
        return (
          <VStack spacing={4}>
            <FormField
              control={control}
              name="title"
              label={i18n.t('entity.document.labels.templateTitle')}
              type="text"
            />

            <FormField
              control={control}
              name="note"
              label={i18n.t('entity.document.labels.note')}
              type="text"
            />

            <VStack spacing={2}>
              <Card
                title={`${title || props.template.title}.${props.documentType}`}
                colorStripe="blue"
                actions={[
                  {
                    type: 'custom',
                    component: (
                      <Upload
                        type="button"
                        accept={props.documentType === 'docx' ? DOCX_MIME_TYPE : HTML_MIME_TYPE}
                        uploadText={i18n.t('entity.vehicle.labels.override')}
                        uploadState={match(isLoading)
                          .with(true, always(UploadState.Uploading))
                          .otherwise(always(UploadState.Idle))}
                        errorIcon="navigation/cancel"
                        uploadingIcon="action/hourglass_empty"
                        uploadIcon="file/upload"
                        customRequest={handleUploadFile}
                      />
                    ),
                  },
                ]}
              />
            </VStack>

            <ButtonGroup align="right">
              <FormButton
                variant="secondary"
                title={i18n.t('general.actions.discard')}
                control={control}
                onClick={closeCurrentDialog}
              />
              <FormButton
                type="submit"
                variant="primary"
                control={control}
                isDisabled={isLoading}
                title={i18n.t('general.actions.saveChanges')}
              />
            </ButtonGroup>
          </VStack>
        );
      }}
    </Form>
  );
}

const schema = z.object({
  title: z.string().nonempty(),
  note: z.string().nullable().optional(),
});
